import api from './api';
import { ProductoFoto } from '../utils/productImage';
import type { ArticuloInfo360Indicadores } from '../pages/inventario/info360/types';
import { asPdfBlobOrThrow } from '../utils/apiError';

export interface Producto {
    codigoArticulo: string;
    descripcion: string;
    codBarra?: string | null;
    costo?: number;
    /** Precio de lista con IVA (Stock.Precio). */
    precio?: number;
    /** Precio neto (sin IVA), mapea a Stock.PrecioSinIva en la API. */
    precioVenta?: number;
    imagen?: string | null;
    fotos?: ProductoFoto[];
    tipoArticulo?: string | null;
    idUnidadMedida?: string | null;
    um?: string | null;
    unidadMedida?: string | null;
    relacionPesoUnid?: number | null;
    umrefPed?: string | null;
    umrefPrep?: string | null;
    umrefFac?: string | null;
    umrefStock?: string | null;
    // otros campos que pueda tener el producto
}

export interface ProductoSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    /**
     * Excluye artículos INA (Inactivo). No debe ocultar DIS.
     * DIS se controla con modoBloqueoArtDiscontinuados (modo 1: listar, no agregar).
     */
    excluirInactivos?: boolean;
    /**
     * true = no listar tipo DIS.
     * En facturación se manda false para que el usuario vea DIS (modo 1 los deja no seleccionables).
     */
    excluirDiscontinuados?: boolean;
    /** 0 permitir DIS · 1 listar DIS no seleccionables · 2 listar DIS (confirmación UI). */
    modoBloqueoArtDiscontinuados?: 0 | 1 | 2;
    /** VENTAS | COMPRAS: el backend lee BLOQUEA_ART_DISCONTINUADOS de esa sección. */
    seccionDocumento?: 'VENTAS' | 'COMPRAS';
    /** Uno o más códigos TipoArticulo separados por coma (p. ej. "PT" o "MP,PT"). */
    tiposArticulo?: string;
}

export interface GetProductoOptions {
    /**
     * true = rechazar solo INA (HTTP 409).
     * DIS se decide con modoBloqueoArtDiscontinuados / seccionDocumento.
     */
    soloOperativo?: boolean;
    /** 0 permitir DIS · 1 HTTP 409 si DIS · 2 devolver el producto (confirmación en UI). */
    modoBloqueoArtDiscontinuados?: 0 | 1 | 2;
    /** VENTAS | COMPRAS: el backend resuelve el parámetro de esa sección. */
    seccionDocumento?: 'VENTAS' | 'COMPRAS';
}

/** Parámetros para búsquedas en flujos que no usan BLOQUEA_ART_DISCONTINUADOS. */
export const PARAMS_PRODUCTO_OPERATIVO = { excluirInactivos: true } as const;

export const OPTIONS_PRODUCTO_OPERATIVO: GetProductoOptions = { soloOperativo: true };

interface ProductoPagedResult {
    items: Producto[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

interface ProductoBulkError {
    indice: number;
    codigoArticulo: string;
    mensaje: string;
}

interface ProductoBulkResult {
    exitosos: number;
    fallidos: number;
    errores: ProductoBulkError[];
}

export interface ResolverCodigoBarraResult {
    codigoArticulo: string;
    descripcion: string;
    cantidad: number;
    precioUnitario: number;
    um?: string | null;
    codBarra?: string | null;
    plu?: string | null;
    origen: string;
    codigoIngresado: string;
    producto?: unknown;
}

interface UploadProductoFotoInput {
    archivo: File;
    esPrincipal?: boolean;
}

const productoService = {
    getProducto: async (codigoArticulo: string, options?: GetProductoOptions): Promise<Producto> => {
        const params: Record<string, string | boolean> = {};
        if (typeof options?.soloOperativo === 'boolean') {
            params.soloOperativo = options.soloOperativo ? 'true' : 'false';
        }
        if (options?.modoBloqueoArtDiscontinuados != null) {
            params.modoBloqueoArtDiscontinuados = String(options.modoBloqueoArtDiscontinuados);
        }
        if (options?.seccionDocumento) {
            params.seccionDocumento = options.seccionDocumento;
        }
        const response = await api.get<Producto>(`/api/productos/${encodeURIComponent(codigoArticulo)}`, {
            params: Object.keys(params).length ? params : undefined,
        });
        return response.data;
    },
    getProductoInfo360: async (codigoArticulo: string): Promise<ArticuloInfo360Indicadores> => {
        const response = await api.get<ArticuloInfo360Indicadores>(
            `/api/Productos/${encodeURIComponent(codigoArticulo)}/info-360`,
        );
        return response.data;
    },
    getProductosInfo360Semaforo: async (codigos: string[]): Promise<Array<{
        codigoArticulo: string;
        severidad: string;
        titulo: string;
        coberturaDias: number;
        demandaMensual6m: number;
        leadTimeDias: number;
    }>> => {
        const unicos = Array.from(new Set(codigos.map((c) => c.trim()).filter(Boolean)));
        if (unicos.length === 0) return [];
        const response = await api.post(`/api/Productos/info-360/semaforo`, { codigos: unicos });
        return response.data ?? [];
    },
    resolverCodigoBarra: async (
        codigo: string,
        nroLista?: string,
        options?: {
            soloOperativo?: boolean;
            modoBloqueoArtDiscontinuados?: 0 | 1 | 2;
            seccionDocumento?: 'VENTAS' | 'COMPRAS';
        },
    ): Promise<ResolverCodigoBarraResult> => {
        const response = await api.post<ResolverCodigoBarraResult>('/api/productos/resolver-codigo-barras', {
            codigo,
            nroLista: nroLista?.trim() || undefined,
            soloOperativo: options?.soloOperativo !== false,
            modoBloqueoArtDiscontinuados: options?.modoBloqueoArtDiscontinuados,
            seccionDocumento: options?.seccionDocumento,
        });
        return response.data;
    },
    getEtiquetaPdf: async (codigoArticulo: string, copias: number): Promise<Blob> => {
        const response = await api.post(
            `/api/productos/${encodeURIComponent(codigoArticulo)}/etiqueta-pdf`,
            {},
            { params: { copias: Math.max(1, copias) }, responseType: 'blob' },
        );
        return asPdfBlobOrThrow(response.data, 'El servidor no pudo generar las etiquetas');
    },
    actualizarCantidad: async (codigoArticulo: string, cantidad: number, tipoAjuste: string, nroUbicacionOrigen?: number, nroUbicacionDestino?: number) => {
        const response = await api.put(`/api/productos/actualizarCantidad`, {
            codigoArticulo,
            cantidad,
            tipoAjuste,
            nroUbicacionOrigen,
            nroUbicacionDestino
        });
        return response.data;
    },
    getProductos: async (params: ProductoSearchParams): Promise<ProductoPagedResult> => {
        const query: Record<string, string | number | boolean | undefined> = {
            searchTerm: params.searchTerm,
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            tiposArticulo: params.tiposArticulo,
        };
        if (typeof params.excluirInactivos === 'boolean') {
            query.excluirInactivos = params.excluirInactivos ? 'true' : 'false';
        }
        if (typeof params.excluirDiscontinuados === 'boolean') {
            query.excluirDiscontinuados = params.excluirDiscontinuados ? 'true' : 'false';
        }
        if (params.modoBloqueoArtDiscontinuados != null) {
            query.modoBloqueoArtDiscontinuados = String(params.modoBloqueoArtDiscontinuados);
        }
        if (params.seccionDocumento) {
            query.seccionDocumento = params.seccionDocumento;
        }
        const response = await api.get<ProductoPagedResult>('/api/Productos', { params: query });
        return response.data;
    },

    /**
     * Producto cuyo CodBarra coincide exactamente (GET /api/productos/por-codigo-barra).
     * 404 = no hay producto. El alta/edición sigue validando unicidad en el servidor (409).
     */
    buscarProductoPorCodigoBarra: async (
        codBarra: string,
    ): Promise<{ codigoArticulo: string; descripcion: string } | null> => {
        const valor = (codBarra || '').trim();
        if (!valor) return null;
        try {
            const response = await api.get<{ codigoArticulo?: string; descripcion?: string }>(
                '/api/productos/por-codigo-barra',
                { params: { codBarra: valor } },
            );
            const codigoArticulo = (response.data?.codigoArticulo || '').trim();
            if (!codigoArticulo) return null;
            return {
                codigoArticulo,
                descripcion: (response.data?.descripcion || '').trim(),
            };
        } catch (error: unknown) {
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status === 404) return null;
            throw error;
        }
    },

    /** Devuelve el otro producto si el código de barras ya está en uso. */
    codigoBarraEnUsoPorOtro: async (
        codBarra: string,
        excludeCodigoArticulo?: string | null,
    ): Promise<{ codigoArticulo: string; descripcion: string } | null> => {
        const found = await productoService.buscarProductoPorCodigoBarra(codBarra);
        if (!found) return null;
        const actual = (excludeCodigoArticulo || '').trim().toUpperCase();
        if (actual && found.codigoArticulo.trim().toUpperCase() === actual) return null;
        return found;
    },
    createProducto: async (producto: Producto) => {
        const response = await api.post('/api/productos', producto);
        return response.data;
    },
    createProductosBulk: async (productos: any[]): Promise<ProductoBulkResult> => {
        const response = await api.post<ProductoBulkResult>('/api/Productos/Bulk', productos);
        return response.data;
    },
    getFotos: async (codigoArticulo: string): Promise<ProductoFoto[]> => {
        const response = await api.get<ProductoFoto[]>(`/api/productos/${codigoArticulo}/fotos`);
        return response.data ?? [];
    },
    uploadFoto: async (codigoArticulo: string, input: UploadProductoFotoInput): Promise<ProductoFoto> => {
        const formData = new FormData();
        formData.append('archivo', input.archivo);
        if (typeof input.esPrincipal === 'boolean') {
            formData.append('esPrincipal', String(input.esPrincipal));
        }

        const response = await api.post<ProductoFoto>(`/api/productos/${codigoArticulo}/fotos`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },
    setFotoPrincipal: async (codigoArticulo: string, fotoId: number) => {
        await api.put(`/api/productos/${codigoArticulo}/fotos/${fotoId}/principal`);
    },
    deleteFoto: async (codigoArticulo: string, fotoId: number) => {
        await api.delete(`/api/productos/${codigoArticulo}/fotos/${fotoId}`);
    },

    getEquivalenciasUm: async (codigoArticulo: string): Promise<UmEquivalenciaDto[]> => {
        const response = await api.get(
            `/api/productos/${encodeURIComponent(codigoArticulo)}/equivalencias-um`
        );
        return parseEquivalenciasUmResponse(response.data).equivalencias;
    },

    /**
     * Equivalencias + UM de logística del artículo.
     * Las refs de Logística no vienen en el DTO operativo: se completan
     * siempre desde la ficha completa del producto.
     */
    getEquivalenciasUmDetalle: async (codigoArticulo: string): Promise<EquivalenciasUmDetalle> => {
        const codigo = String(codigoArticulo || '').trim();
        const response = await api.get(
            `/api/productos/${encodeURIComponent(codigo)}/equivalencias-um`
        );
        const parsed = parseEquivalenciasUmResponse(response.data);

        let umrefPed = parsed.umrefPed;
        let umrefFac = parsed.umrefFac;
        let umrefPrep = parsed.umrefPrep;
        let umrefStock = parsed.umrefStock;
        let umBase = parsed.umBase;

        // Ficha completa (sin soloOperativo): ahí están umrefPed / umrefFac / etc.
        try {
            const producto = await api.get(`/api/productos/${encodeURIComponent(codigo)}`);
            const p = (producto.data ?? {}) as Record<string, unknown>;
            const lowerMap = new Map(Object.keys(p).map((k) => [k.toLowerCase(), k]));
            const pick = (...keys: string[]) => {
                for (const k of keys) {
                    const real = lowerMap.get(k.toLowerCase());
                    if (!real) continue;
                    const raw = p[real];
                    if (raw == null) continue;
                    if (typeof raw === 'object') {
                        const nested = raw as Record<string, unknown>;
                        const id = String(
                            nested.idUnidadMedida ??
                                nested.IdUnidadMedida ??
                                nested.codigo ??
                                nested.descripcion ??
                                '',
                        ).trim();
                        if (id) return id;
                        continue;
                    }
                    if (String(raw).trim()) return String(raw).trim();
                }
                return '';
            };
            umrefPed = umrefPed || pick('umrefPed', 'UmrefPed', 'umRefPed', 'umPedido', 'UmPedido') || null;
            umrefFac = umrefFac || pick('umrefFac', 'UmrefFac', 'umRefFac', 'umFactura', 'UmFactura') || null;
            umrefPrep = umrefPrep || pick('umrefPrep', 'UmrefPrep', 'umRefPrep') || null;
            umrefStock = umrefStock || pick('umrefStock', 'UmrefStock', 'umRefStock') || null;
            const umNav = p.unidadMedida;
            const fromNav =
                umNav && typeof umNav === 'object'
                    ? String(
                          (umNav as Record<string, unknown>).idUnidadMedida ??
                              (umNav as Record<string, unknown>).IdUnidadMedida ??
                              '',
                      ).trim()
                    : '';
            umBase =
                umBase ||
                pick('idUnidadMedida', 'IdUnidadMedida', 'um', 'Um') ||
                fromNav ||
                null;
        } catch {
            /* sin producto */
        }

        return {
            equivalencias: parsed.equivalencias,
            umrefPed: umrefPed || null,
            umrefFac: umrefFac || null,
            umrefPrep: umrefPrep || null,
            umrefStock: umrefStock || null,
            umBase: umBase || null,
        };
    },

    replaceEquivalenciasUm: async (codigoArticulo: string, equivalencias: UmEquivalenciaDto[]) => {
        await api.put(`/api/productos/${encodeURIComponent(codigoArticulo)}/equivalencias-um`, {
            equivalencias: equivalencias.map((e) => ({
                umOrig: e.umOrig,
                umDest: e.umDest,
                cantDest: e.cantDest,
            })),
        });
    },

    getProveedoresCosto: async (codigoArticulo: string): Promise<ProductoProveedorCostoDto[]> => {
        const response = await api.get<ProductoProveedorCostoDto[]>(
            `/api/productos/${encodeURIComponent(codigoArticulo)}/proveedores-costo`
        );
        return response.data ?? [];
    },

    replaceProveedoresCosto: async (codigoArticulo: string, proveedores: ProductoProveedorCostoDto[]) => {
        await api.put(`/api/productos/${encodeURIComponent(codigoArticulo)}/proveedores-costo`, {
            proveedores,
        });
    },

    getUmCompra: async (codigoArticulo: string, codigoProveedor?: number | null): Promise<UmCompraResolveDto> => {
        const response = await api.get<UmCompraResolveDto>(
            `/api/productos/${encodeURIComponent(codigoArticulo)}/um-compra`,
            {
                params:
                    codigoProveedor != null && codigoProveedor > 0
                        ? { codigoProveedor }
                        : undefined,
            }
        );
        return response.data;
    },
};

export interface UmEquivalenciaDto {
    umOrig: string;
    umDest: string;
    cantDest: number;
}

export interface EquivalenciasUmDetalle {
    equivalencias: UmEquivalenciaDto[];
    umrefPed?: string | null;
    umrefFac?: string | null;
    umrefPrep?: string | null;
    umrefStock?: string | null;
    umBase?: string | null;
}

function readWrappedUm(root: Record<string, unknown>, ...keys: string[]): string {
    const lowerMap = new Map(Object.keys(root).map((k) => [k.toLowerCase(), k]));
    for (const key of keys) {
        const real = lowerMap.get(key.toLowerCase());
        if (!real) continue;
        const v = root[real];
        if (v != null && String(v).trim()) return String(v).trim();
    }
    return '';
}

/** Acepta array directo o wrapper, y propiedades camelCase / PascalCase. */
export function normalizeEquivalenciasUm(data: unknown): UmEquivalenciaDto[] {
    return parseEquivalenciasUmResponse(data).equivalencias;
}

export function parseEquivalenciasUmResponse(data: unknown): EquivalenciasUmDetalle {
    const root = data as Record<string, unknown> | unknown[] | null | undefined;
    const list: unknown[] = Array.isArray(root)
        ? root
        : Array.isArray((root as Record<string, unknown> | undefined)?.equivalencias)
          ? ((root as Record<string, unknown>).equivalencias as unknown[])
          : Array.isArray((root as Record<string, unknown> | undefined)?.Equivalencias)
            ? ((root as Record<string, unknown>).Equivalencias as unknown[])
            : [];

    const equivalencias = list
        .map((item) => {
            const o = (item ?? {}) as Record<string, unknown>;
            const umOrig = String(o.umOrig ?? o.UmOrig ?? '').trim();
            const umDest = String(o.umDest ?? o.UmDest ?? '').trim();
            const cantDest = Number(
                o.cantDest ?? o.CantDest ?? o.cantidadDestino ?? o.CantidadDestino ?? o.factor ?? o.Factor,
            );
            return { umOrig, umDest, cantDest };
        })
        .filter((e) => e.umOrig && e.umDest && Number.isFinite(e.cantDest) && e.cantDest > 0);

    if (!root || Array.isArray(root)) {
        return { equivalencias };
    }

    const r = root as Record<string, unknown>;
    return {
        equivalencias,
        umrefPed: readWrappedUm(r, 'umrefPed', 'UmrefPed', 'umRefPed', 'umPedido', 'UmPedido') || null,
        umrefFac: readWrappedUm(r, 'umrefFac', 'UmrefFac', 'umRefFac', 'umFactura', 'UmFactura') || null,
        umrefPrep: readWrappedUm(r, 'umrefPrep', 'UmrefPrep', 'umRefPrep') || null,
        umrefStock: readWrappedUm(r, 'umrefStock', 'UmrefStock', 'umRefStock') || null,
        umBase: readWrappedUm(r, 'umBase', 'UmBase', 'idUnidadMedida', 'IdUnidadMedida', 'um', 'Um') || null,
    };
}

export interface ProductoProveedorCostoDto {
    codigoProveedor: string;
    codigoArticuloProveedor: string;
    costo?: number | null;
    costoBruto?: number | null;
    descuento1?: number | null;
    descuento2?: number | null;
    descuento3?: number | null;
    descuento4?: number | null;
    descuento5?: number | null;
    umCompra?: string | null;
}

export interface UmCompraResolveDto {
    codigoArticulo: string;
    umCompra: string;
    umBase: string;
    factorABase: number;
    umsDisponibles: string[];
}

export default productoService; 